/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as Ron from 'blb-ui-types/ron';
import { Field } from 'react-final-form';
import { styles } from 'blb-inputs/common';
import { composeValidators, isEmptyValue, TextInput } from '@blend/enterprise-components';
import { FinalFormInputConfig } from 'blb-ui-types/helpers';
import { WrappedInput } from 'blb-components/WrappedInput';
import { CredentialAttrKeys, CredentialAttrMeta, UserCredentialType } from 'blb-constants/ron';
import { futureDateValidator, pastDateValidator } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/dateUtils';

const getInputConfig = (
  credentialType: UserCredentialType,
  attrType: Ron.API.CredentialAttrKey,
): FinalFormInputConfig<TextInput.Props, string> => {
  const meta = CredentialAttrMeta[attrType];
  const validators = [];
  if (meta.required) {
    validators.push(isEmptyValue('Required'));
  }
  if (meta.type === 'date') {
    if (meta.pastDate) {
      validators.push(pastDateValidator);
    }
    if (meta.futureDate) {
      validators.push(futureDateValidator);
    }
  }
  return {
    component: WrappedInput,
    containerStyles: [styles.fieldWrapper],
    customComponentProps: { disabled: credentialType === UserCredentialType.DIGITAL_CERTIFICATE, type: meta.type },
    inputComponent: TextInput,
    label: CredentialAttrMeta[attrType].label,
    labelSize: 'sm',
    name: attrType,
    validate: validators ? composeValidators(...validators) : undefined,
  };
};

export namespace CredentialAttributeInputFields {
  export interface Props {
    credentialType: UserCredentialType;
  }
}

/** This component renders list of form fields for editing notary credential attributes. */
export const CredentialAttributeInputFields: React.FC<CredentialAttributeInputFields.Props> = ({ credentialType }) => {
  const keys = CredentialAttrKeys[credentialType];
  const fields = keys.map(key => getInputConfig(credentialType, key));
  return (
    <>
      <Field component={() => <div />} key="fileId" name="fileId" />
      <Field component={() => <div />} key="fileName" name="fileName" />
      {fields.map(field => (
        <Field key={field.name} {...field} />
      ))}
    </>
  );
};
